import { createContext, useContext, type ReactNode } from 'react';

/**
 * Identity context derived from the auth probe (`useAuditProbe`).
 *
 * - `isPlatformAdmin` is true whenever the probe returned 200, because the
 *   backend gates `GET /api/v1/audit/events` behind the `platform_admin`
 *   role (see src/http.rs:481-492). 401 / 403 never reach this context — the
 *   ProtectedRoute redirects to /login or NoAccessPage first.
 *
 * - `principalDisplayName` is null in V1: the backend currently exposes no
 *   `/api/v1/me` (or equivalent) endpoint, and the `denox_session` cookie is
 *   HttpOnly so the SPA cannot decode it client-side. The TopBar avatar
 *   degrades to the literal "P" until that endpoint exists.
 *   TODO(v2): replace with the real principal once /api/v1/me lands.
 */
export interface IdentityContextValue {
  isPlatformAdmin: boolean;
  principalDisplayName: string | null;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

export function IdentityProvider({
  value,
  children,
}: {
  value: IdentityContextValue;
  children: ReactNode;
}) {
  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

/**
 * Read the identity context. Returns a safe default outside the protected
 * subtree so leaf components (e.g. shared headers) don't have to special-case
 * the public / login routes.
 */
export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (ctx) return ctx;
  return { isPlatformAdmin: false, principalDisplayName: null };
}
